import React from "react";
import { Box, Container, Typography, Grid, Paper } from "@mui/material";
import { Visibility, Flag, EmojiPeople } from "@mui/icons-material";

const AboutContent = () => {
  return (
    <Box sx={{ py: { xs: 6, md: 10 } }}>
      <Container maxWidth="lg">
        {/* Company Info */}
        <Box sx={{ mb: 8, textAlign: "center" }}>
          <Typography
            variant="h2"
            gutterBottom
            sx={{ fontWeight: 700, color: "primary.main", mb: 3 }}
          >
            About MRLOANS
          </Typography>
          <Typography
            variant="h6"
            color="text.secondary"
            sx={{ maxWidth: "800px", mx: "auto", mb: 4 }}
          >
            We are a trusted financial services provider committed to helping
            South Africans access quick and reliable cash loans when they need
            it most.
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ maxWidth: "900px", mx: "auto", lineHeight: 1.8 }}
          >
            Founded with the mission to make borrowing simple, transparent, and
            accessible, we've helped thousands of customers achieve their
            financial goals. Our streamlined application process, competitive
            rates, and excellent customer service set us apart in the industry.
            We believe everyone deserves access to fair and affordable credit,
            and we work hard every day to make that a reality.
          </Typography>
        </Box>

        {/* Mission, Vision, Values */}
        <Grid container spacing={4} sx={{ mb: 8 }}>
          <Grid item xs={12} md={4}>
            <Paper
              elevation={2}
              sx={{
                p: 4,
                height: "100%",
                textAlign: "center",
                transition: "transform 0.3s",
                "&:hover": {
                  transform: "translateY(-8px)",
                },
              }}
            >
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 80,
                  height: 80,
                  borderRadius: "50%",
                  bgcolor: "primary.main",
                  color: "white",
                  mb: 2,
                }}
              >
                <Flag sx={{ fontSize: 40 }} />
              </Box>
              <Typography
                variant="h4"
                gutterBottom
                sx={{ fontWeight: 600, color: "primary.main" }}
              >
                Our Mission
              </Typography>
              <Typography variant="body1" color="text.secondary">
                To provide fast, secure, and transparent financial solutions
                that empower individuals to achieve their goals and overcome
                financial challenges.
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} md={4}>
            <Paper
              elevation={2}
              sx={{
                p: 4,
                height: "100%",
                textAlign: "center",
                transition: "transform 0.3s",
                "&:hover": {
                  transform: "translateY(-8px)",
                },
              }}
            >
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 80,
                  height: 80,
                  borderRadius: "50%",
                  bgcolor: "secondary.main",
                  color: "white",
                  mb: 2,
                }}
              >
                <Visibility sx={{ fontSize: 40 }} />
              </Box>
              <Typography
                variant="h4"
                gutterBottom
                sx={{ fontWeight: 600, color: "secondary.main" }}
              >
                Our Vision
              </Typography>
              <Typography variant="body1" color="text.secondary">
                To be South Africa's most trusted and customer-friendly cash
                loan provider, known for our integrity, innovation, and
                commitment to financial inclusion.
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} md={4}>
            <Paper
              elevation={2}
              sx={{
                p: 4,
                height: "100%",
                textAlign: "center",
                transition: "transform 0.3s",
                "&:hover": {
                  transform: "translateY(-8px)",
                },
              }}
            >
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 80,
                  height: 80,
                  borderRadius: "50%",
                  bgcolor: "primary.main",
                  color: "white",
                  mb: 2,
                }}
              >
                <EmojiPeople sx={{ fontSize: 40 }} />
              </Box>
              <Typography
                variant="h4"
                gutterBottom
                sx={{ fontWeight: 600, color: "primary.main" }}
              >
                Our Values
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Transparency, integrity, customer focus, and innovation guide
                everything we do. We treat our customers with respect and
                dignity.
              </Typography>
            </Paper>
          </Grid>
        </Grid>

        {/* Why Choose Us */}
        <Box
          sx={{
            bgcolor: "background.default",
            p: { xs: 3, md: 6 },
            borderRadius: 2,
          }}
        >
          <Typography
            variant="h3"
            gutterBottom
            sx={{ fontWeight: 700, color: "primary.main", mb: 3 }}
          >
            Why Choose Us?
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                  ✓ Fast and Easy Application
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Our online application takes just minutes to complete, with
                  approval within hours.
                </Typography>
              </Box>
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                  ✓ Transparent Pricing
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  No hidden fees or surprises. We're upfront about all costs
                  from the start.
                </Typography>
              </Box>
              <Box>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                  ✓ Flexible Repayment Options
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Choose a repayment plan that fits your budget and financial
                  situation.
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                  ✓ Excellent Customer Service
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Our friendly team is here to help you every step of the way.
                </Typography>
              </Box>
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                  ✓ Secure and Private
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Your personal and financial information is protected with the
                  highest security standards.
                </Typography>
              </Box>
              <Box>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                  ✓ Registered and Compliant
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  We operate in full compliance with South African financial
                  regulations.
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Container>
    </Box>
  );
};

export default AboutContent;
